<template>
  <div class="fulfillment-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.rewards.fulfillment_title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.rewards.fulfillment_subtitle') }}</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.rewards.fulfillment_filter_status') }}</label>
        <select v-model="filters.status" class="filter-select" @change="() => fetchRedemptions()">
          <option value="">{{ $t('admin.rewards.fulfillment_status_pending_shipped') }}</option>
          <option value="pending">{{ $t('admin.rewards.fulfillment_status_pending') }}</option>
          <option value="shipped">{{ $t('admin.rewards.fulfillment_status_shipped') }}</option>
          <option value="cancelled">{{ $t('admin.rewards.fulfillment_status_cancelled') }}</option>
        </select>
      </div>
      <div class="filter-stats">
        <span class="stat-chip pending">{{ $t('admin.rewards.fulfillment_pending_count', { n: pendingCount }) }}</span>
      </div>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">{{ $t('admin.rewards.fulfillment_loading') }}</div>
      <div v-else-if="redemptions.length === 0" class="empty-state">
        <p>{{ $t('admin.rewards.fulfillment_empty') }}</p>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>{{ $t('admin.rewards.fulfillment_col_player') }}</th>
            <th>{{ $t('admin.rewards.fulfillment_col_reward') }}</th>
            <th>{{ $t('admin.rewards.fulfillment_col_points') }}</th>
            <th>{{ $t('admin.rewards.col_status') }}</th>
            <th>{{ $t('admin.rewards.fulfillment_col_redeemed_at') }}</th>
            <th>{{ $t('admin.rewards.col_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in redemptions" :key="r.id">
            <td>
              <div class="player-cell">
                <div class="player-name">{{ r.user?.name ?? '—' }}</div>
                <div class="player-email">{{ r.user?.email ?? '' }}</div>
              </div>
            </td>
            <td>
              <div class="reward-cell">
                <div class="reward-name">{{ r.reward_item?.name ?? '—' }}</div>
                <div v-if="r.admin_notes" class="reward-notes">{{ r.admin_notes }}</div>
              </div>
            </td>
            <td class="points-cell">{{ r.points_spent.toLocaleString() }} {{ $t('admin.rewards.pts') }}</td>
            <td>
              <span :class="['status-badge', `status-${r.status}`]">{{ r.status }}</span>
            </td>
            <td class="date-cell">{{ formatDate(r.created_at) }}</td>
            <td class="actions-cell">
              <template v-if="r.status === 'pending'">
                <button class="btn btn-sm btn-primary" @click="openShipModal(r)">{{ $t('admin.rewards.fulfillment_btn_ship') }}</button>
                <button class="btn btn-sm btn-danger" @click="confirmCancel(r)">{{ $t('admin.rewards.fulfillment_btn_cancel') }}</button>
              </template>
              <template v-else-if="r.status === 'shipped'">
                <button class="btn btn-sm btn-danger" @click="confirmCancel(r)">{{ $t('admin.rewards.fulfillment_btn_cancel') }}</button>
              </template>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="pagination && pagination.last_page > 1" class="pagination-bar">
        <button
          class="btn btn-sm"
          :disabled="pagination.current_page <= 1"
          @click="goToPage(pagination.current_page - 1)"
        >
          {{ $t('admin.rewards.fulfillment_btn_previous') }}
        </button>
        <span class="page-info">{{ $t('admin.affiliates.page_of', { current: pagination.current_page, total: pagination.last_page }) }}</span>
        <button
          class="btn btn-sm"
          :disabled="pagination.current_page >= pagination.last_page"
          @click="goToPage(pagination.current_page + 1)"
        >
          {{ $t('admin.rewards.fulfillment_btn_next') }}
        </button>
      </div>
    </div>

    <!-- Ship Modal -->
    <div v-if="showShipModal" class="modal-overlay" @mousedown.self="closeShipModal">
      <div class="modal-panel" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h2 class="modal-title">{{ $t('admin.rewards.fulfillment_ship_title') }}</h2>
          <button class="modal-close" title="Close" @click="closeShipModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            {{ $t('admin.rewards.fulfillment_ship_desc', { reward: shipTarget?.reward_item?.name, player: shipTarget?.user?.name }) }}
          </p>
          <label class="form-label">
            {{ $t('admin.rewards.fulfillment_ship_notes_label') }}
            <textarea
              v-model="shipNotes"
              class="form-textarea"
              rows="3"
              :placeholder="$t('admin.rewards.fulfillment_ship_notes_placeholder')"
            ></textarea>
          </label>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closeShipModal">{{ $t('admin.rewards.btn_cancel') }}</button>
            <button class="btn btn-primary" :disabled="shipLoading" @click="submitShip">
              {{ shipLoading ? $t('admin.rewards.fulfillment_btn_shipping') : $t('admin.rewards.fulfillment_btn_confirm_shipped') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <div v-if="showCancelModal" class="modal-overlay" @mousedown.self="closeCancelModal">
      <div class="modal-panel modal-panel--sm" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h2 class="modal-title">{{ $t('admin.rewards.fulfillment_cancel_title') }}</h2>
          <button class="modal-close" title="Close" @click="closeCancelModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            {{ $t('admin.rewards.fulfillment_cancel_desc', { reward: cancelTarget?.reward_item?.name, player: cancelTarget?.user?.name, points: cancelTarget?.points_spent.toLocaleString() }) }}
          </p>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closeCancelModal">{{ $t('admin.rewards.fulfillment_btn_keep') }}</button>
            <button class="btn btn-danger" :disabled="cancelLoading" @click="submitCancel">
              {{ cancelLoading ? $t('admin.rewards.fulfillment_btn_cancelling') : $t('admin.rewards.fulfillment_btn_yes_cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import apiClient from '@/api/client'

interface RedemptionUser {
  id: number
  name: string
  email: string
}

interface RedemptionItem {
  id: number
  name: string
  reward_type: string
  point_cost: number
}

interface Redemption {
  id: number
  user_id: number
  user: RedemptionUser | null
  reward_item_id: number
  reward_item: RedemptionItem | null
  points_spent: number
  status: string
  fulfilled_at: string | null
  admin_notes: string | null
  created_at: string
  updated_at: string
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

const redemptions = ref<Redemption[]>([])
const pagination = ref<PaginationMeta | null>(null)
const loading = ref(false)

const filters = ref({
  status: '',
  page: 1,
})

const pendingCount = computed(() =>
  filters.value.status === '' || filters.value.status === 'pending'
    ? redemptions.value.filter((r) => r.status === 'pending').length
    : 0,
)

// ---------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------

async function fetchRedemptions(resetPage = true) {
  if (resetPage) filters.value.page = 1
  loading.value = true
  try {
    const params: Record<string, string | number> = { page: filters.value.page, per_page: 15 }
    if (filters.value.status) params.status = filters.value.status

    const res = await apiClient.get<{
      data: Redemption[]
      current_page: number
      last_page: number
      per_page: number
      total: number
    }>('/admin/api/rewards/fulfillment', { params })

    redemptions.value = res.data.data
    pagination.value = {
      current_page: res.data.current_page,
      last_page: res.data.last_page,
      per_page: res.data.per_page,
      total: res.data.total,
    }
  } catch {
    redemptions.value = []
  } finally {
    loading.value = false
  }
}

function goToPage(page: number) {
  filters.value.page = page
  fetchRedemptions(false)
}

// ---------------------------------------------------------------
// Ship modal
// ---------------------------------------------------------------

const showShipModal = ref(false)
const shipTarget = ref<Redemption | null>(null)
const shipNotes = ref('')
const shipLoading = ref(false)

function openShipModal(r: Redemption) {
  shipTarget.value = r
  shipNotes.value = r.admin_notes ?? ''
  showShipModal.value = true
}

function closeShipModal() {
  showShipModal.value = false
  shipTarget.value = null
  shipNotes.value = ''
}

async function submitShip() {
  if (!shipTarget.value) return
  shipLoading.value = true
  try {
    const res = await apiClient.post<Redemption>(
      `/admin/api/rewards/fulfillment/${shipTarget.value.id}/ship`,
      { admin_notes: shipNotes.value || null },
    )
    // Update in list
    const idx = redemptions.value.findIndex((r) => r.id === res.data.id)
    if (idx !== -1) redemptions.value[idx] = res.data
    closeShipModal()
  } catch {
    // error handled by global interceptor
  } finally {
    shipLoading.value = false
  }
}

// ---------------------------------------------------------------
// Cancel modal
// ---------------------------------------------------------------

const showCancelModal = ref(false)
const cancelTarget = ref<Redemption | null>(null)
const cancelLoading = ref(false)

function confirmCancel(r: Redemption) {
  cancelTarget.value = r
  showCancelModal.value = true
}

function closeCancelModal() {
  showCancelModal.value = false
  cancelTarget.value = null
}

async function submitCancel() {
  if (!cancelTarget.value) return
  cancelLoading.value = true
  try {
    const res = await apiClient.post<Redemption>(
      `/admin/api/rewards/fulfillment/${cancelTarget.value.id}/cancel`,
    )
    const idx = redemptions.value.findIndex((r) => r.id === res.data.id)
    if (idx !== -1) redemptions.value[idx] = res.data
    closeCancelModal()
  } catch {
    // error handled by global interceptor
  } finally {
    cancelLoading.value = false
  }
}

// ---------------------------------------------------------------
// Utils
// ---------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

onMounted(() => fetchRedemptions())
</script>

<style scoped>
.fulfillment-page {
  padding: 24px;
  max-width: 1200px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

/* Filters */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
}

.filter-select {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  padding: 6px 10px;
  outline: none;
}

.filter-stats {
  display: flex;
  gap: 8px;
}

.stat-chip {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
}

.stat-chip.pending {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
}

/* Table */
.table-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.loading-state,
.empty-state {
  padding: 40px;
  text-align: center;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.15);
  text-align: left;
}

.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-primary, #fff);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  vertical-align: middle;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tr:hover td {
  background: rgba(255, 255, 255, 0.02);
}

.player-cell .player-name {
  font-weight: 600;
}

.player-cell .player-email {
  font-size: 11px;
  color: var(--text-secondary, #b1bad3);
}

.reward-cell .reward-name {
  font-weight: 600;
}

.reward-cell .reward-notes {
  font-size: 11px;
  color: var(--text-secondary, #b1bad3);
  margin-top: 2px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.points-cell {
  font-weight: 600;
  color: var(--accent-primary, #00e701);
}

.date-cell {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
}

.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.04em;
}

.status-pending {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
}

.status-shipped {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.status-fulfilled {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-cancelled {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.page-info {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  padding: 8px 16px;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 12px;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl, 16px);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.modal-panel--sm {
  max-width: 380px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
  background: rgba(255, 255, 255, 0.06);
}

.modal-body {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-desc {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
  line-height: 1.5;
}

.modal-desc strong {
  color: var(--text-primary, #fff);
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.form-textarea {
  background: var(--bg-primary, #0f1e2a);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  padding: 10px 12px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.form-textarea:focus {
  border-color: var(--accent-primary, #00e701);
}
</style>
